/**
 * @fileoverview Auto-rotating announcement bar.
 *
 * Registered as a `<announcement-bar>` custom element so each section instance
 * manages its own lifecycle. With two or more messages (and `data-autorotate`
 * present) it crossfades between them on the interval set by `data-speed`
 * (seconds). Rotation pauses on hover/focus and while the tab is hidden, and is
 * skipped entirely when the visitor prefers reduced motion. Because the browser
 * calls `connectedCallback`/`disconnectedCallback` when Shopify swaps the
 * section in the theme editor, no manual `shopify:section:*` wiring is needed.
 */

(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(
        this.querySelectorAll('.announcement-bar__message')
      );

      const shouldRotate =
        this.hasAttribute('data-autorotate') &&
        this.messages.length > 1 &&
        !reduceMotion.matches;

      if (!shouldRotate) return;

      const speed = parseFloat(this.getAttribute('data-speed')) || 4;
      this.interval = Math.max(2, speed) * 1000;
      this.current = 0;

      this.advance = this.advance.bind(this);
      this.start = this.start.bind(this);
      this.stop = this.stop.bind(this);

      this.addEventListener('mouseenter', this.stop);
      this.addEventListener('mouseleave', this.start);
      this.addEventListener('focusin', this.stop);
      this.addEventListener('focusout', this.start);
      document.addEventListener('visibilitychange', this.handleVisibility);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.removeEventListener('mouseenter', this.stop);
      this.removeEventListener('mouseleave', this.start);
      this.removeEventListener('focusin', this.stop);
      this.removeEventListener('focusout', this.start);
      document.removeEventListener('visibilitychange', this.handleVisibility);
    }

    handleVisibility = () => {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    };

    start() {
      if (this.timer || document.hidden) return;
      this.timer = window.setInterval(this.advance, this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      const previous = this.messages[this.current];
      this.current = (this.current + 1) % this.messages.length;
      const next = this.messages[this.current];

      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');

      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
    }
  }

  if (!customElements.get('announcement-bar')) {
    customElements.define('announcement-bar', AnnouncementBar);
  }
})();
